import type {RefCallback} from 'react'

import columnStyles from '../frames/BoardColumnFrame.module.css'
import rowStyles from '../frames/BoardRowFrame.module.css'
import styles from './BoardWindow.module.css'

export type BoardWindowSpacerProps = {
  /** Pixels to reserve, or `null` when this end of the axis has nothing left out. */
  size: number | null
  /** Which way the axis runs: `inline` across the columns, `block` down the rows. */
  axis: 'inline' | 'block'
}

/**
 * The part of an axis that is not rendered, as a box of exactly its size.
 *
 * It is what makes the scrollbars describe the whole board rather than the part of it
 * that happens to be on screen, and it is a box in the ordinary flow rather than an
 * offset written onto the items, which is the choice everything else depends on. Offset
 * items would need a container as tall as every row for the rows to be positioned inside,
 * and a row asking for `100%` of that container would get the height of the whole board
 * instead of one band — the geometry the board's `minmax(0, 1fr)` track already exists to
 * avoid. Left in flow, a row resolves its height against exactly the box it always did.
 */
export function BoardWindowSpacer({size, axis}: BoardWindowSpacerProps) {
  if (size === null) return null

  return (
    <div
      aria-hidden="true"
      className={styles.Spacer}
      style={axis === 'inline' ? {inlineSize: `${size}px`} : {blockSize: `${size}px`}}
    />
  )
}

export type BoardMetricsProbesProps = {
  columnRef: RefCallback<HTMLElement>
  rowRef: RefCallback<HTMLElement>
}

/**
 * Where the board's two stated lengths are resolved to pixels, for `useBoardMetrics` to
 * read.
 *
 * Each probe wears the class of the frame it stands in for rather than restating that
 * frame's length, so the two cannot drift apart and neither can disagree about which edges
 * of the box the length describes.
 *
 * They are empty, and that is the point: a probe has no contents for its size to be
 * measured from, so what comes back is the length the board stated and nothing else.
 *
 * They belong inside the strip of rows because a row's height may be a percentage, and a
 * percentage resolves against whatever contains it. The wrapper fills that strip exactly,
 * so the row probe reads `100%` of the same box a real row does.
 */
export function BoardMetricsProbes({columnRef, rowRef}: BoardMetricsProbesProps) {
  return (
    <div aria-hidden="true" className={styles.Metrics}>
      <div ref={columnRef} className={columnStyles.ColumnFrame} />
      <div ref={rowRef} className={rowStyles.RowFrame} />
    </div>
  )
}
